import React, { useEffect } from 'react';
import { getIntl } from '../../../../../utils/intl';
import { useGoBack } from '../../../../../utils/appNavigationNames';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import Stack from '../../../../v2BaseComponents/Stack';
import useProfilesWithPreferences, { PreferencesWithProfiles } from './useProfilesWithPreferences';
import { ActionButtonLayout } from '../../../../v2BaseComponents/ActionButtonLayout';
import MaxWidthRestrictedContent from '../../../../v2BaseComponents/MaxWidthRestrictedContent';
import ProfileGroupConfiguration from './ProfileGroupConfiguration';
import MainContentContainer from '../../../../v2BaseComponents/MainContentContainer';
import { useLoadedUser } from '../../../../../hooks/api/useUser';
import AccountHeader from '../../../../shared/Account/AccountHeader';

const intl = getIntl('PreferencesScreen', {
    save: 'Save',
    cancel: 'Cancel',
});

export type PreferencesScreenOwnProps = {};

export type PreferencesScreenProps = PreferencesScreenOwnProps;

const PreferencesScreenContent: React.FC<PreferencesScreenProps & { onReset: () => void }> = ({ onReset }) => {
    const { user } = useLoadedUser({});
    const _goBack = useGoBack();
    const { isLoading, isUpdating, profilesWithPreferences, update } = useProfilesWithPreferences({
        onUpdateSuccess: onReset,
    });
    const [isDirty, setIsDirty] = React.useState(false);
    const [workingValues, setWorkingValues] = React.useState<PreferencesWithProfiles | null>(null);
    useEffect(() => {
        if (profilesWithPreferences) {
            setIsDirty(false);
            setWorkingValues(profilesWithPreferences);
        }
    }, [profilesWithPreferences]);
    const resetWorkingValue = () => {
        setIsDirty(false);
        setWorkingValues(profilesWithPreferences);
    };
    const handleWorkingValuesChange = (newWorkingValues: PreferencesWithProfiles) => {
        setIsDirty(true);
        setWorkingValues(newWorkingValues);
    };

    const isLoadingContent = isLoading || isUpdating || !workingValues;

    return (
        <AnimateLoadedContent isLoading={isLoadingContent}>
            {() => (
                <>
                    <AccountHeader user={user} />
                    <ActionButtonLayout
                        scrollable
                        whiteBackground
                        actions={
                            isDirty
                                ? [
                                      {
                                          title: intl('cancel'),
                                          onPress: resetWorkingValue,
                                          isDisabled: isUpdating,
                                          icon: 'XCircle',
                                          variant: 'secondary',
                                          testID: 'cancel-preferences-button',
                                      },
                                      {
                                          title: intl('save'),
                                          onPress: () => {
                                              if (workingValues) {
                                                  update(workingValues);
                                              }
                                          },
                                          isDisabled: isUpdating,
                                          icon: 'CheckCircle',
                                          testID: 'save-preferences-button',
                                      },
                                  ]
                                : undefined
                        }
                    >
                        <MaxWidthRestrictedContent>
                            <MainContentContainer>
                                <Stack vertical fullWidth space={16}>
                                    <ProfileGroupConfiguration
                                        value={workingValues?.userProfiles || []}
                                        onChange={(value) =>
                                            handleWorkingValuesChange({ ...workingValues!, userProfiles: value })
                                        }
                                        isDisabled={isUpdating}
                                    />
                                    <ProfileGroupConfiguration
                                        value={workingValues?.collaboratorProfiles || []}
                                        onChange={(value) =>
                                            handleWorkingValuesChange({
                                                ...workingValues!,
                                                collaboratorProfiles: value,
                                            })
                                        }
                                        isDisabled={isUpdating}
                                        isCollaborators
                                    />
                                </Stack>
                            </MainContentContainer>
                        </MaxWidthRestrictedContent>
                    </ActionButtonLayout>
                </>
            )}
        </AnimateLoadedContent>
    );
};

const ProfilesScreen: React.FC<PreferencesScreenProps> = () => {
    const [resetKey, setResetKey] = React.useState(0);
    return <PreferencesScreenContent key={resetKey} onReset={() => setResetKey((_) => _ + 1)} />;
};

export default React.memo(ProfilesScreen);
